import { useState } from "react";
import { Stack } from "@mui/material";
import PropTypes from "prop-types";
import ChartBlock, { chartHasSeriesData } from "../pricing-reports/ChartBlock";
import DesignAccordion from "../pricing-reports/design/Accordion";
import {
  flyerLineChartOptions,
  flyerProvinceBarChartOptions,
  stackedBarChartOptions,
} from "./flyerReportChartUtils";
import {
  emptyPromotionalFrequencyLineChart,
  emptyProvinceOfferCountChart,
  emptyRetailerStackedBarChart,
} from "./flyerReportChartDefaults";

function FlyerReportChartsContent({
  selectedRetailer,
  selectedProvince,
  retailerStackedBarChart,
  promotionalFrequencyLineChart,
  provinceOfferCountChart,
  isRetailerChartLoading,
  isFrequencyChartLoading,
  isProvinceChartLoading,
  retailerChartError,
  frequencyChartError,
  provinceChartError,
}) {
  return (
    <Stack spacing={2}>
      {!selectedRetailer ? (
        <ChartBlock
          title="Promotion count by retailer"
          subtitle="Promotion count stacked by discount type (regular price, % off, dollars off)."
          chartType="bar"
          chart={retailerStackedBarChart.chart}
          chartOptions={stackedBarChartOptions}
          hasData={chartHasSeriesData(retailerStackedBarChart.chart)}
          isLoading={isRetailerChartLoading}
          error={retailerChartError}
          emptyMessage="No promotions by retailer for the current filters."
        />
      ) : null}

      <ChartBlock
        title="Promotional frequency over time"
        subtitle="Monthly promotion count within the panel date range."
        chartType="line"
        chart={promotionalFrequencyLineChart.chart}
        chartOptions={flyerLineChartOptions}
        hasData={chartHasSeriesData(promotionalFrequencyLineChart.chart)}
        isLoading={isFrequencyChartLoading}
        error={frequencyChartError}
        emptyMessage="No promotions in the selected date range."
      />

      {selectedProvince == null ? (
        <ChartBlock
          title="Promotion count by province"
          subtitle="Provinces sorted by promotion count; provinces with no promotions appear at the end."
          chartType="bar"
          chart={provinceOfferCountChart.chart}
          chartOptions={flyerProvinceBarChartOptions}
          hasData={chartHasSeriesData(provinceOfferCountChart.chart)}
          isLoading={isProvinceChartLoading}
          error={provinceChartError}
          emptyMessage="No promotions by province for the current filters."
        />
      ) : null}
    </Stack>
  );
}

/**
 * Three promotional charts for Insights → Flyer Report, wrapped in a collapsed accordion.
 * Per-chart loading, error, and empty states — chart errors are not shown at panel level.
 */
export default function FlyerReportCharts({
  selectedRetailer = null,
  selectedProvince = null,
  retailerStackedBarChart = emptyRetailerStackedBarChart,
  promotionalFrequencyLineChart = emptyPromotionalFrequencyLineChart,
  provinceOfferCountChart = emptyProvinceOfferCountChart,
  isRetailerChartLoading = false,
  isFrequencyChartLoading = false,
  isProvinceChartLoading = false,
  retailerChartError = null,
  frequencyChartError = null,
  provinceChartError = null,
}) {
  const [flyerChartsAccordionExpanded, setFlyerChartsAccordionExpanded] = useState(false);

  return (
    <DesignAccordion
      title="Flyer charts"
      subtitle="- Promotion counts and frequency for the current filters."
      expanded={flyerChartsAccordionExpanded}
      onChange={setFlyerChartsAccordionExpanded}
    >
      <FlyerReportChartsContent
        selectedRetailer={selectedRetailer}
        selectedProvince={selectedProvince}
        retailerStackedBarChart={retailerStackedBarChart}
        promotionalFrequencyLineChart={promotionalFrequencyLineChart}
        provinceOfferCountChart={provinceOfferCountChart}
        isRetailerChartLoading={isRetailerChartLoading}
        isFrequencyChartLoading={isFrequencyChartLoading}
        isProvinceChartLoading={isProvinceChartLoading}
        retailerChartError={retailerChartError}
        frequencyChartError={frequencyChartError}
        provinceChartError={provinceChartError}
      />
    </DesignAccordion>
  );
}

const chartShapePropType = PropTypes.shape({
  chart: PropTypes.object,
  retailerChartHasAnyData: PropTypes.bool,
  frequencyLineHasAnyData: PropTypes.bool,
  provinceChartHasAnyData: PropTypes.bool,
});

const provinceFilterPropType = PropTypes.shape({
  value: PropTypes.string,
  label: PropTypes.string,
});

FlyerReportChartsContent.propTypes = {
  selectedRetailer: PropTypes.string,
  selectedProvince: provinceFilterPropType,
  retailerStackedBarChart: chartShapePropType,
  promotionalFrequencyLineChart: chartShapePropType,
  provinceOfferCountChart: chartShapePropType,
  isRetailerChartLoading: PropTypes.bool,
  isFrequencyChartLoading: PropTypes.bool,
  isProvinceChartLoading: PropTypes.bool,
  retailerChartError: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  frequencyChartError: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  provinceChartError: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

FlyerReportCharts.propTypes = {
  selectedRetailer: PropTypes.string,
  selectedProvince: provinceFilterPropType,
  retailerStackedBarChart: chartShapePropType,
  promotionalFrequencyLineChart: chartShapePropType,
  provinceOfferCountChart: chartShapePropType,
  isRetailerChartLoading: PropTypes.bool,
  isFrequencyChartLoading: PropTypes.bool,
  isProvinceChartLoading: PropTypes.bool,
  retailerChartError: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  frequencyChartError: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  provinceChartError: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};
